<template>
  <div class="fantasy_index">
    <page-header :title="$tc('fantasy.name', 1)" />
    <page-content>
      <div class="row">
        <div class="col-lg-8 offset-lg-2">
          <ntm-block :collapsable="false">
            <template slot="options">
              <multiselect
                :allowEmpty="false"
                style="min-width: 150px;"
                class="d-inline-block"
                track-by="uuid"
                v-model="currentSeason"
                :options="seasonOptions"
                @search-change="getOptions"
                :customLabel="customLabel"
              />
            </template>

            <le-loading v-if="loading" />

            <div v-else-if="!model.data?.length" class="text-center py-4 text-muted">
              {{ $t('noData') }}
            </div>

            <div v-else class="table-responsive">
              <ntm-table :show-columns="showColumns" :data="model" @pageChange="setPage" :loading="loading">
                <template v-slot:status="{row}">
                  <span v-if="row.hasFantasy" class="badge badge-success">
                    {{ $t('fantasy.on') }}
                  </span>
                  <span v-else class="badge badge-secondary">
                    {{ $t('fantasy.off') }}
                  </span>
                </template>
                <template v-slot:actions="{row}">
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    @click="openFantasyModal(row)"
                    v-tooltip="row.hasFantasy ? $t('fantasy.edit') : $t('fantasy.create')"
                  >
                    <i class="fa" :class="row.hasFantasy ? 'fa-pencil' : 'fa-plus'"></i>
                  </button>
                </template>
              </ntm-table>
            </div>
          </ntm-block>
        </div>
      </div>

      <modal name="fantasy-modal" :adaptive="true" height="auto">
        <ntm-block :collapsable="false" class="overflow-y-auto" :title="selectedSchedule?.hasFantasy ? $t('fantasy.edit') : $t('fantasy.create')" :style="fantasyLoading ? { minHeight: '50dvh' } : {}">
          <template #options>
            <button type="button" class="btn btn-sm btn-light" @click="$modal.hide('fantasy-modal')" v-tooltip="$t('close')">
              <i class="fa fa-times"></i>
            </button>
          </template>

          <le-loading v-if="fantasyLoading" />

          <template v-else>
            <div class="mb-3 pb-3 border-bottom">
              <div class="mb-2">
                {{ selectedSchedule?.name }} - {{ currentSeason?.name }}
              </div>
            </div>

            <div v-if="selectedSchedule?.hasFantasy" class="mb-3">
              <span class="badge badge-pill badge-info text-white mr-1">
                <i class="fa fa-users"></i> {{ fantasyModel?.fantasyLeague?.fantasyRegistrationsCount }}
              </span>
              <span class="badge badge-pill badge-info text-white">
                <i class="fa fa-play"></i> {{ fantasyModel?.fantasyLeague?.fantasyGameScoresCount }}
              </span>
            </div>

            <formly-form
              :form="fantasyCreateForm"
              :model="fantasyCreateData"
              :fields="fantasyCreateFields"
            />

            <div class="mt-3 mb-3">
              <button class="btn btn-success mr-2" @click="upsertFantasy()">
                {{ selectedSchedule?.hasFantasy ? $t('save') : $t('fantasy.create') }}
              </button>
              <button v-if="selectedSchedule?.hasFantasy" class="btn btn-danger" @click="deleteFantasy()">
                {{ $t('fantasy.delete') }}
              </button>
            </div>
          </template>
        </ntm-block>
      </modal>
    </page-content>
  </div>
</template>

<script>
import ScheduleService from '@/services/schedule.service'
import seasonIndexMixin from '@/mixins/season.index.mixin'
import Multiselect from 'vue-multiselect'
import LeLoading from '@/components/ntm/LeLoading.vue'
import NtmTable from '@/components/ntm/ntmTable.vue'
import fantasyCreateFormDefinition from '@/views/schedules/formDefinitions/fantasyCreateFormDefinition'

export default {
  name: 'Fantasy',
  mixins: [seasonIndexMixin],
  components: { Multiselect, LeLoading, NtmTable },
  data () {
    return {
      selectedSchedule: null,
      fantasyModel: null,
      fantasyCreateForm: {},
      fantasyCreateData: {},
      fantasyCreateFields: fantasyCreateFormDefinition,
      fantasyLoading: false,
      showColumns: [
        { name: 'name', prop: 'name' },
        { name: 'status' }
      ]
    }
  },
  computed: {
    seasonOptions () {
      return this.$store.state.seasonOptions
    },
    currentSeason: {
      get () {
        return this.$store.state.currentSeason
      },
      set (value) {
        this.$store.commit('setCurrentSeason', value)
      }
    },
    config () {
      return this.$store.state.config
    }
  },
  mounted () {
    this.getOptions()
    this.fetchData()
  },
  watch: {
    currentSeason () {
      this.fetchData()
    }
  },
  methods: {
    customLabel (o) {
      return o.name ?? ''
    },
    getOptions (q = '') {
      this.$store.dispatch('getSeasonOptions', q)
    },
    fetchData () {
      this.loading = true
      ScheduleService.index('', this.$store.state.currentSeason?.uuid).then((response) => {
        const data = Array.isArray(response.data) ? response.data : (response.data.data || [])
        this.model = {
          data: data,
          meta: {
            current_page: 1,
            last_page: 1
          }
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },
    openFantasyModal (item) {
      this.selectedSchedule = item
      this.fantasyLoading = true
      this.$modal.show('fantasy-modal')
      ScheduleService.show(item.uuid).then((response) => {
        this.fantasyModel = response.data
        if (!this.fantasyModel.fantasyLeague) {
          this.fantasyCreateData = {
            weeklyGamePredictionLimit: this.config.fantasy.weeklyGamePredictionLimit,
            teamPredictionScoreMultiplier: this.config.fantasy.teamPredictionScoreMultiplier,
            teamPredictionScoreMinValue: this.config.fantasy.teamPredictionScoreMinValue,
            starterPlayersCount: this.config.fantasy.roster.starterPlayersCount,
            benchPlayersCount: this.config.fantasy.roster.benchPlayersCount,
            minPlayerValue: this.config.fantasy.roster.minPlayerValue
          }
        } else {
          this.fantasyCreateData = this.fantasyModel.fantasyLeague
        }
        this.fantasyLoading = false
      })
    },
    upsertFantasy () {
      ScheduleService.upsertFantasy(this.selectedSchedule.uuid, this.fantasyCreateData).then(() => {
        this.$modal.hide('fantasy-modal')
        this.fetchData()
      })
    },
    deleteFantasy () {
      ScheduleService.deleteFantasy(this.selectedSchedule.uuid).then(() => {
        this.$modal.hide('fantasy-modal')
        this.fetchData()
      })
    }
  }
}
</script>
